import ReportHub from '@tencent/data-report-hub-sdk';
import _ from 'lodash';
import { toJS } from 'mobx';
import globalStore from '@/stores';
import { REACT_APP_PUBLIC_URL } from '@/constants/Constants';
import { matchPath } from 'react-router-dom';
import { EMBEDDED_PATH_MAP } from '@/app/embedded';
import { SINGLE_PATH_MAP } from '@/app/single';
import { getAIPageModule } from '@/ai-assistant';

// 公共字段，其他other_fields字段都放在extra_data中
const COMMON_FIELDS = [
  'bm_id',
  'company_id',
  'agency_uid',
  'login_type',
  'roles',
  'real_name_verify_status',
  'relation_key',
  'relation_value',
  'sub_module', // 页面内部区分子模块
];

const reportHub = new ReportHub({
  appId: '62d7617032b9e1015f9058a0',
  enableReport: process.env.DEPLOY_ENV === 'production',
  spa: true,
  include: ['#root'],
  urlHandler: () => {
    const { pathname, hash } = window.location;
    // 路径片段存在数字时替换为{id}
    const filteredPath = pathname
      .split('/')
      .filter((segment) => !!segment)
      .map((segment) => (/\d/.test(segment) ? '{id}' : segment))
      .join('/')
      .replace(/^/, '/');
    // 对外iframe页面路径增加hash
    if (filteredPath.startsWith(`${REACT_APP_PUBLIC_URL}/embedded.html`)) {
      return `${filteredPath}${hash}`;
    }
    return filteredPath;
  },
  beforeUpload: (data: any) => {
    // other_fields中非公共字段放在extra_data中
    const extraDataFieldsOrigin = data.other_fields.filter((item: any) => !COMMON_FIELDS.includes(item.field));
    const extraDataFields: Record<string, any> = {};
    extraDataFieldsOrigin.forEach((item: any) => {
      if (item.field === 'extra_data') { // extra_data是json字符串，需要解析
        try {
          const extraObj = JSON.parse(item.value || '{}');
          Object.keys(extraObj).forEach((key) => {
            extraDataFields[key] = extraObj[key];
          });
        } catch (err) {
        }
      } else { // 其他字段直接赋值到extra_data中
        extraDataFields[item.field] = item.value;
      }
    });
    if (!_.isEmpty(extraDataFields)) {
      data.other_fields = data.other_fields.filter((item: any) => COMMON_FIELDS.includes(item.field));
      data.other_fields.push({ field: 'extra_data', value: JSON.stringify(extraDataFields) });
    }

    // 数据上传增加页面模块
    const pathTreeMap = globalStore.account?.siteMap?.pathTreeMap || {};
    const url = data.path?.replace(REACT_APP_PUBLIC_URL, '');
    let pageModule: string[] = [];
    if (url === '' || url === '/') { // 页面加载时认为进入首页
      pageModule = ['首页'];
    } else if (url.startsWith('/analysis/ai')) { // ai分析页面路由单独处理
      pageModule = getAIPageModule(url);
    } else if (url.startsWith('/embedded.html#')) { // 对外提供iframe页面
      pageModule = getPageModule(url, EMBEDDED_PATH_MAP);
    } else if (url.startsWith('/single')) { // single页面路由单独处理
      pageModule = getPageModule(url, SINGLE_PATH_MAP);
    } else { // 内部页面
      pageModule = getPageModule(url, pathTreeMap);
    }
    // 特殊处理 AI 助理
    if (data.action?.includes('ai-assistant') && pageModule.every((name) => name !== 'AI 助理')) {
      pageModule.push('AI 助理');
    }
    // 如果包换sub_module，追加到path和page_module后
    const subModule = data.other_fields.find((item: any) => item.field === 'sub_module')?.value;
    if (subModule) {
      pageModule.push(subModule);
      data.path = `${data.path}/${subModule}`;
      data.other_fields = data.other_fields.filter((item: any) => item.field !== 'sub_module');
    }
    data.other_fields.push({ field: 'page_module', value: _.compact(pageModule).join('-') });

    return data;
  },
});

const getPageModule = (url: string, treeMap: Record<string, string[]>) => {
  let pageModule: string[] = [];
  if (treeMap[url]) { // 路径完全匹配
    pageModule = treeMap[url];
  } else { // 寻找匹配路径
    Object.keys(treeMap).some((path: string) => {
      if (matchPath(url, path)) {
        pageModule = treeMap[path];
        return true;
      }
      return false;
    });
  }
  return toJS(pageModule);
};

export default reportHub;
